import React, { useState, useEffect, useRef, useCallback } from 'react';
import Dialog from '../../organisms/Dialog';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField from '../../molecules/SelectTextField';
import appConfig from '../../../config/app-config';

const { address, key } = appConfig.apis.legalApiEnv;

const CATEGORIAS = [
    { value: 'contrato_original', label: 'Contrato original' },
    { value: '1_aditamento', label: '1º Aditamento' },
    { value: '2_aditamento', label: '2º Aditamento' },
    { value: '3_aditamento', label: '3º Aditamento' },
    { value: '4_aditamento', label: '4º Aditamento' },
    { value: '5_aditamento', label: '5º Aditamento' },
    { value: 'alienacao_fiduciaria', label: 'Alienação fiduciária', disabled: true },
    { value: 'conta_escrow', label: 'Conta escrow', disabled: true },
];

const PREFIXOS = [
    { value: 'CPR-F', label: 'CPR-F' },
    { value: 'CPR-F PJ', label: 'CPR-F PJ' },
    { value: 'Cessão', label: 'Cessão' },
];

type Props = {
    show: boolean;
    row: any;
    onClose: () => void;
    onSaved: (fileId: number, contractNumber: string, operationId: string) => void;
};

const CreditOperationModalForm: React.FC<Props> = ({ show, row, onClose, onSaved }) => {
    const [categoria, setCategoria] = useState('contrato_original');
    const [prefixo, setPrefixo] = useState('CPR-F');
    const [contractDigits, setContractDigits] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [selectedContract, setSelectedContract] = useState<any>(null);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const isAditamento = categoria.includes('aditamento');
    const aditamentoNum = isAditamento ? Number.parseInt(categoria.charAt(0)) : 0;

    useEffect(() => {
        if (!show || !row) return;
        const cn = row.contract_number || '';
        const digits = (s: string) => s.replace(/\D/g, '').slice(0, 6);
        if (cn.startsWith('C')) {
            setPrefixo('Cessão');
            setContractDigits(digits(cn.slice(1)));
        } else if (cn.startsWith('PJ-')) {
            setPrefixo('CPR-F PJ');
            setContractDigits(digits(cn.slice(3)));
        } else {
            setPrefixo('CPR-F');
            setContractDigits(digits(cn));
        }
        setError('');
        setCategoria('contrato_original');
        setSearchTerm('');
        setSearchResults([]);
        setSelectedContract(null);
    }, [show, row]);

    const getHeaders = () => ({
        'Content-Type': 'application/json',
        'x-api-key': key ?? '',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
    });

    const searchContracts = useCallback(async (term: string) => {
        if (term.length < 2) { setSearchResults([]); return; }
        setSearching(true);
        try {
            const res = await fetch(`${address}/credit-operations/search/${encodeURIComponent(term)}`, { headers: getHeaders() });
            const data = await res.json();
            setSearchResults(Array.isArray(data) ? data : []);
        } finally {
            setSearching(false);
        }
    }, []);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setSelectedContract(null);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => { searchContracts(value).catch(() => {}); }, 300);
    };

    const selectContract = (contract: any) => {
        setSelectedContract(contract);
        setSearchTerm(contract.contract_number);
        setSearchResults([]);
    };

    const buildContractNumber = () => {
        if (isAditamento && selectedContract) {
            const base = selectedContract.contract_number.replace(/-\d+$/, '');
            return `${base}-${aditamentoNum}`;
        }
        const padded = contractDigits.padStart(6, '0');
        if (prefixo === 'Cessão') return `C${padded}`;
        if (prefixo === 'CPR-F PJ') return `PJ-${padded}`;
        return padded;
    };

    const canSave = isAditamento ? !!selectedContract : !!contractDigits.trim();

    const saveContract = async () => {
        if (!row || !canSave) return;
        const contractNumber = buildContractNumber();
        setSaving(true);
        setError('');
        const headers = getHeaders();
        try {
            let res: Response;
            if (row.operation_id) {
                res = await fetch(`${address}/credit-operations/${row.operation_id}`, {
                    method: 'PATCH', headers, body: JSON.stringify({ contract_number: contractNumber, file_id: row.id }),
                });
            } else {
                res = await fetch(`${address}/credit-operations/file`, {
                    method: 'POST', headers, body: JSON.stringify({ contract_number: contractNumber, file_id: row.id }),
                });
            }
            const result = await res.json();
            if (!res.ok) {
                setError(result.error || result.message || 'Erro ao salvar');
                return;
            }
            onSaved(row.id, result.contract_number, result.operation_id);
            onClose();
        } catch (e: any) {
            setError(e.message || 'Erro de conexão');
        } finally {
            setSaving(false);
        }
    };

    const handleDigitsChange = (value: string) => {
        setContractDigits(value.replace(/\D/g, '').slice(0, 6));
    };

    return (
        <Dialog
            id="vincular-contrato"
            show={show}
            onClose={onClose}
            header="Vincular Contrato"
            size="lg"
            body={
                <div className="p-6 space-y-3" style={{ width: 600 }}>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">Pasta Raiz</label>
                        <p className="text-sm text-gray-800">{row?.root_folder}</p>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">Processo</label>
                        <div className="text-sm text-gray-800">
                            {row?.process?.split('/').map((part: string, i: number) => (
                                <div key={`${part}-${i}`} style={{ paddingLeft: `${i * 16}px` }} className="flex items-center gap-1">
                                    <span className="text-gray-400">{i > 0 ? '└' : '📁'}</span>
                                    <span>{part}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">Arquivo</label>
                        <p className="text-sm text-gray-800 break-all">{row?.pdf_file}</p>
                    </div>

                    <SelectTextField
                        id="categoria"
                        label="Categoria"
                        value={categoria}
                        onChange={e => setCategoria(e.target.value)}
                        items={CATEGORIAS}
                        emptyValue="__none__"
                        emptyLabel=""
                    />

                    {isAditamento ? (
                        <div className="relative">
                            <TextEdit
                                label="Buscar contrato original"
                                value={searchTerm}
                                onChange={e => handleSearchChange(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && saveContract()}
                                helperText={selectedContract ? `Resultado: ${buildContractNumber()}` : undefined}
                                autoFocus
                            />
                            {searching && <p className="text-xs text-gray-400 mt-1">Buscando...</p>}
                            {searchResults.length > 0 && !selectedContract && (
                                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded shadow-lg mt-1 max-h-48 overflow-y-auto">
                                    {searchResults.map((c: any) => (
                                        <div
                                            key={c.contract_number}
                                            className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer flex justify-between"
                                            onClick={() => selectContract(c)}
                                        >
                                            <span className="font-mono font-medium">{c.contract_number}</span>
                                            <span className="text-gray-500 text-xs truncate ml-2">{c.issuer_name || c.operation_type}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex gap-2 items-end">
                            <SelectTextField
                                id="prefixo"
                                label="Tipo"
                                value={prefixo}
                                onChange={e => setPrefixo(e.target.value)}
                                items={PREFIXOS}
                                emptyValue="__none__"
                                emptyLabel=""
                                className="w-32"
                            />
                            <TextEdit
                                label="Número do Contrato"
                                inputMode="numeric"
                                maxLength={6}
                                value={contractDigits}
                                onChange={e => handleDigitsChange(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && saveContract()}
                                helperText={contractDigits ? `Resultado: ${buildContractNumber()}` : undefined}
                                autoFocus
                            />
                        </div>
                    )}
                    {error && <p className="text-sm text-red-600">{error}</p>}
                </div>
            }
            footer={
                <div className="flex gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                        Cancelar
                    </button>
                    <button
                        onClick={saveContract}
                        disabled={saving || !canSave}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        {saving ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            }
        />
    );
};

export default CreditOperationModalForm;
